/**
 * The AI's fingerprint of one item of a plan — a phase, a day or an exercise (ai-plan-builder R18).
 *
 * A proposal change carries the fingerprint its item had in the draft the proposal was made from,
 * so the builder can tell an item the trainer changed since: the stamps differ. **The rule lives on
 * both sides of the wire** and is pinned by the fixture the backend emits
 * (`train-with-me-app/docs/contracts/ai-fingerprints.json`, vendored byte for byte into
 * `__fixtures__/`) and `aiFingerprint.test.ts` replays. The backend's half is
 * `Modules/Ai/Domain/Fingerprints.cs`; neither side is free to change the rule alone.
 *
 * The rule: the item's own members in the SAVE request's shape, without `id`, `order`, `slotToken`
 * and the items inside it (a day's sessions, a phase's days). Members holding nothing or the save
 * request's default are left out — null, an empty string, an empty list or object, false, zero, a
 * `"Working"` set type, an `"AbsoluteWeight"` prescription mode — so a side that sends a default and
 * one that leaves it out agree. Keys in ordinal order, numbers in shortest decimal form without an
 * exponent, strings escaped as `JSON.stringify` escapes them. The stamp is the first sixteen hex
 * digits of the SHA-256 of that text's UTF-8 bytes.
 *
 * ── Why the hash is computed here, synchronously ────────────────────────────────────────────
 * Web Crypto's `digest` is asynchronous, and two readers of this module cannot wait: the document
 * store refuses an outdated change INSIDE the apply operation (R18 is enforced where the tree is
 * written, not by a button a second caller could forget), and "a value the AI wrote" has to be
 * answerable during a render (R15). So SHA-256 is implemented below, in the FIPS 180-4 form, and the
 * fixture's stamps are what prove it.
 *
 * Which item of the draft a fingerprint is taken over is `draftToRequest.ts`'s save shapes, so a
 * draft item is stamped exactly as the snapshot the backend stamped it from was serialised.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import { daySaveShape, daySnapshotShape, exerciseSaveShape, phaseSaveShape, phaseSnapshotShape } from './draftToRequest';
import type { DayDraft, ExerciseDraft, PhaseDraft } from './planDraft.types';

/** The item kinds a fingerprint is taken over, and the members holding the items inside each. */
const CHILDREN = { exercise: [], day: ['sessions'], phase: ['days'] } as const;

/**
 * The kinds a fingerprint is taken over: an item's own members, or (for the target of a removal) a
 * day or a phase WITH the items inside it — an edit anywhere inside a day Buddy proposes to remove
 * makes that removal outdated.
 */
export type FingerprintedKind = keyof typeof CHILDREN | 'dayWithItems' | 'phaseWithItems';

const IDENTITY = ['id', 'order', 'slotToken'];

/** Members whose value is the save request's own default, left out like an empty one. */
const DEFAULT_STRINGS: Readonly<Record<string, string>> = {
  setType: 'Working',
  prescriptionMode: 'AbsoluteWeight',
};

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Whether a member is left out. Judged on the member's own value, never on what is left of it. */
function isEmptyMember(key: string, value: unknown): boolean {
  if (value === null || value === undefined || value === false || value === 0 || value === '') return true;
  if (typeof value === 'string') return DEFAULT_STRINGS[key] === value;
  if (Array.isArray(value)) return value.length === 0;
  if (isRecord(value)) return Object.keys(value).length === 0;
  return false;
}

/**
 * A number as its shortest decimal, never in exponent form. `String` already gives the shortest
 * round-tripping digits; it only switches to an exponent below 1e-6 and from 1e21, and those are
 * written out in full here.
 */
function decimal(value: number): string {
  const text = String(value);
  const exponent = text.indexOf('e');
  if (exponent === -1) return text;
  const sign = text.startsWith('-') ? '-' : '';
  const mantissa = text.slice(sign.length, exponent);
  const power = Number(text.slice(exponent + 1));
  const point = mantissa.indexOf('.');
  const digits = mantissa.replace('.', '');
  const integerLength = (point === -1 ? mantissa.length : point) + power;
  if (integerLength <= 0) return `${sign}0.${'0'.repeat(-integerLength)}${digits}`;
  return `${sign}${digits.padEnd(integerLength, '0')}`;
}

function write(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(write).join(',')}]`;
  if (isRecord(value)) {
    const members = Object.keys(value)
      .filter((key) => !isEmptyMember(key, value[key]))
      // Ordinal: UTF-16 code units, which is what `Array.prototype.sort` compares by default.
      .sort()
      .map((key) => `${JSON.stringify(key)}:${write(value[key])}`);
    return `{${members.join(',')}}`;
  }
  if (typeof value === 'number') return decimal(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'string') return JSON.stringify(value);
  return 'null';
}

/**
 * The text an item is stamped from. Exported for the fixture replay and for whoever has to work out
 * why the two sides disagree about one item.
 */
export function fingerprintCanonical(kind: FingerprintedKind, node: Readonly<Record<string, unknown>>): string {
  if (kind === 'dayWithItems' || kind === 'phaseWithItems') return write(withItems(node));
  const omitted = new Set<string>([...IDENTITY, ...CHILDREN[kind]]);
  const own = Object.fromEntries(Object.entries(node).filter(([key]) => !omitted.has(key)));
  return write(own);
}

/**
 * An item with everything inside it, identity left out at every depth: a list whose items all carry
 * an order is put in that order first (a stable sort, so equal orders keep the list's own), then
 * `id`, `order` and `slotToken` go.
 */
function orderOf(item: unknown): number {
  return isRecord(item) && typeof item.order === 'number' ? item.order : 0;
}

function withItems(value: unknown): unknown {
  if (Array.isArray(value)) {
    const items: readonly unknown[] = value;
    const ordered = items.every((item) => isRecord(item) && typeof item.order === 'number')
      ? [...items].sort((a, b) => orderOf(a) - orderOf(b))
      : items;
    return ordered.map(withItems);
  }
  if (!isRecord(value)) return value;
  return Object.fromEntries(Object.entries(value).filter(([key]) => !IDENTITY.includes(key)).map(([key, member]) => [key, withItems(member)]));
}

/** The stamp of one item already in the save request's shape — a snapshot node, or a fixture's. */
export function fingerprintOfWireNode(kind: FingerprintedKind, node: Readonly<Record<string, unknown>>): string {
  return sha256Hex(new TextEncoder().encode(fingerprintCanonical(kind, node))).slice(0, 16);
}

export function exerciseFingerprint(exercise: ExerciseDraft): string {
  return fingerprintOfWireNode('exercise', { ...exerciseSaveShape(exercise) });
}

export function dayFingerprint(day: DayDraft): string {
  return fingerprintOfWireNode('day', { ...daySaveShape(day) });
}

export function phaseFingerprint(phase: PhaseDraft): string {
  return fingerprintOfWireNode('phase', { ...phaseSaveShape(phase) });
}

/** The stamp of a day Buddy proposes to remove: the day with everything inside it, as a snapshot carries it. */
export function dayRemovalFingerprint(day: DayDraft): string {
  return fingerprintOfWireNode('dayWithItems', { ...daySnapshotShape(day) });
}

/** The stamp of a phase Buddy proposes to remove: the phase with its days, as a snapshot carries it. */
export function phaseRemovalFingerprint(phase: PhaseDraft): string {
  return fingerprintOfWireNode('phaseWithItems', { ...phaseSnapshotShape(phase) });
}

/**
 * The version a snapshot of the whole plan travels with (ai-plan-builder R13): the stamp of the
 * request itself, written by the same canonical rule, every member kept (ids and orders included,
 * since the snapshot is the whole plan). Two snapshots of the same tree carry the same version
 * whichever store revision or page they were taken from, and any edit gives another; the store's
 * revision cannot say either, since it restarts at every load.
 */
export function snapshotVersion(request: Readonly<Record<string, unknown>>): string {
  return `plan-${sha256Hex(new TextEncoder().encode(write(request))).slice(0, 16)}`;
}

// ── SHA-256 (FIPS 180-4) ─────────────────────────────────────────────────────────────────────

const ROUND_CONSTANTS = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

const INITIAL_HASH = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];

function rotateRight(word: number, bits: number): number {
  return (word >>> bits) | (word << (32 - bits));
}

function sha256Hex(message: Uint8Array): string {
  // The message, a 1 bit, zeros, and its length in bits as a 64-bit big-endian number, padded to a
  // whole number of 64-byte blocks.
  const blockCount = Math.ceil((message.length + 9) / 64);
  const padded = new Uint8Array(blockCount * 64);
  padded.set(message);
  padded[message.length] = 0x80;
  const view = new DataView(padded.buffer);
  const bitLength = message.length * 8;
  view.setUint32(padded.length - 8, Math.floor(bitLength / 0x1_0000_0000));
  view.setUint32(padded.length - 4, bitLength >>> 0);

  const hash = new Uint32Array(INITIAL_HASH);
  const schedule = new Uint32Array(64);
  for (let block = 0; block < blockCount; block += 1) {
    for (let t = 0; t < 16; t += 1) schedule[t] = view.getUint32(block * 64 + t * 4);
    for (let t = 16; t < 64; t += 1) {
      const w15 = schedule[t - 15] ?? 0;
      const w2 = schedule[t - 2] ?? 0;
      const s0 = rotateRight(w15, 7) ^ rotateRight(w15, 18) ^ (w15 >>> 3);
      const s1 = rotateRight(w2, 17) ^ rotateRight(w2, 19) ^ (w2 >>> 10);
      schedule[t] = (schedule[t - 16] ?? 0) + s0 + (schedule[t - 7] ?? 0) + s1;
    }
    let [a = 0, b = 0, c = 0, d = 0, e = 0, f = 0, g = 0, h = 0] = hash;
    for (let t = 0; t < 64; t += 1) {
      const s1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
      const choice = (e & f) ^ (~e & g);
      const temp1 = (h + s1 + choice + (ROUND_CONSTANTS[t] ?? 0) + (schedule[t] ?? 0)) >>> 0;
      const s0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (s0 + majority) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }
    hash[0] = (hash[0] ?? 0) + a;
    hash[1] = (hash[1] ?? 0) + b;
    hash[2] = (hash[2] ?? 0) + c;
    hash[3] = (hash[3] ?? 0) + d;
    hash[4] = (hash[4] ?? 0) + e;
    hash[5] = (hash[5] ?? 0) + f;
    hash[6] = (hash[6] ?? 0) + g;
    hash[7] = (hash[7] ?? 0) + h;
  }
  return Array.from(hash, (word) => word.toString(16).padStart(8, '0')).join('');
}
